/**
 * AI Chatbot Widget
 * 
 * A customer support chatbot using OpenAI API
 * Features:
 * - Streaming responses
 * - Session persistence (localStorage)
 * - Rate limiting awareness
 * - Mobile responsive
 * - Accessibility support
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const config = window.chatbotConfig || {};
  const API_URL = config.apiUrl || '';
  const WELCOME_MESSAGE = config.welcomeMessage || "Hi! How can I help you today?";
  const PLACEHOLDER_TEXT = config.placeholderText || "Type your message...";
  const HEADER_TITLE = config.headerTitle || "Customer Support";
  const HEADER_SUBTITLE = config.headerSubtitle || "We typically reply instantly";
  
  const STORAGE_KEY = 'chatbot_conversation';
  const MAX_HISTORY_LENGTH = 20;

  // ============================================
  // State
  // ============================================
  let isOpen = false;
  let isLoading = false;
  let conversationHistory = [];
  let hasSeenWelcome = false;

  // ============================================
  // DOM Elements
  // ============================================
  const widget = document.getElementById('chatbot-widget');
  const toggle = document.getElementById('chatbot-toggle');
  const container = document.getElementById('chatbot-container');
  const closeBtn = document.getElementById('chatbot-close');
  const messagesContainer = document.getElementById('chatbot-messages');
  const form = document.getElementById('chatbot-form');
  const input = document.getElementById('chatbot-input');
  const sendBtn = document.getElementById('chatbot-send');
  const typingIndicator = document.getElementById('chatbot-typing');
  const notification = document.getElementById('chatbot-notification');
  const errorContainer = document.getElementById('chatbot-error');
  const errorText = document.getElementById('chatbot-error-text');
  const errorClose = document.getElementById('chatbot-error-close');
  const charCurrent = document.getElementById('chatbot-char-current');
  const headerTitle = document.getElementById('chatbot-title');
  const headerSubtitle = document.getElementById('chatbot-subtitle');

  // ============================================
  // Initialization
  // ============================================
  function init() {
    if (!widget || !API_URL) {
      console.warn('Chatbot: Widget element or API URL not found');
      return;
    }

    // Set header text
    if (headerTitle) headerTitle.textContent = HEADER_TITLE;
    if (headerSubtitle) headerSubtitle.textContent = HEADER_SUBTITLE;
    if (input) input.placeholder = PLACEHOLDER_TEXT;

    // Load saved conversation
    loadConversation();

    // Bind events
    bindEvents();

    // Show widget
    widget.setAttribute('aria-hidden', 'false');

    // Show notification if no conversation yet
    if (conversationHistory.length === 0) {
      showNotification();
    }
  }

  // ============================================
  // Event Binding
  // ============================================
  function bindEvents() {
    // Toggle chat
    toggle.addEventListener('click', toggleChat);
    closeBtn.addEventListener('click', closeChat);

    // Form submission
    form.addEventListener('submit', handleSubmit);

    // Input handling
    input.addEventListener('input', handleInputChange);
    input.addEventListener('keydown', handleKeyDown);

    // Error close
    errorClose.addEventListener('click', hideError);

    // Click outside to close
    document.addEventListener('click', handleOutsideClick);

    // Escape to close
    document.addEventListener('keydown', handleEscapeKey);
  }

  // ============================================
  // Chat Toggle
  // ============================================
  function toggleChat() {
    if (isOpen) {
      closeChat();
    } else {
      openChat();
    }
  }

  function openChat() {
    isOpen = true;
    widget.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    container.setAttribute('aria-hidden', 'false');
    hideNotification();

    // Show welcome message if first time
    if (!hasSeenWelcome && conversationHistory.length === 0) {
      addMessage('assistant', WELCOME_MESSAGE);
      hasSeenWelcome = true;
    }

    // Focus input after animation
    setTimeout(() => {
      //input.focus();
      scrollToBottom();
    }, 300);
  }

  function closeChat() {
    isOpen = false;
    widget.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    container.setAttribute('aria-hidden', 'true');
    toggle.focus();
  }

  // ============================================
  // Message Handling
  // ============================================
  function handleSubmit(e) {
    e.preventDefault();

    const message = input.value.trim();
    if (!message || isLoading) return;

    // Clear input
    input.value = '';
    updateCharCount();
    updateSendButton();

    // Add user message
    addMessage('user', message);

    // Send to API
    sendMessage(message);
  }

  function handleInputChange() {
    updateCharCount();
    updateSendButton();
    autoResizeTextarea();
  }

  function handleKeyDown(e) {
    // Send on Enter (but allow Shift+Enter for new line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      form.dispatchEvent(new Event('submit'));
    }
  }

  function updateCharCount() {
    const count = input.value.length;
    charCurrent.textContent = count;
  }

  function updateSendButton() {
    const hasContent = input.value.trim().length > 0;
    sendBtn.disabled = !hasContent || isLoading;
  }

  function autoResizeTextarea() {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 120) + 'px';
  }

  // ============================================
  // API Communication
  // ============================================
  async function sendMessage(message) {
    if (!API_URL) {
      showError('Chat service is not configured.');
      return;
    }

    isLoading = true;
    showTypingIndicator();
    hideError();
    updateSendButton();

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message,
          conversationHistory: conversationHistory.slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 429) {
          throw new Error(errorData.error || 'Too many messages. Please wait a moment.');
        }
        
        throw new Error(errorData.error || 'Something went wrong. Please try again.');
      }

      // Handle streaming response
      await handleStreamingResponse(response);

    } catch (error) {
      console.error('Chatbot error:', error);
      showError(error.message || 'Failed to send message. Please try again.');
    } finally {
      isLoading = false;
      hideTypingIndicator();
      updateSendButton();
    }
  }

  async function handleStreamingResponse(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let assistantMessage = '';
    let messageElement = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                throw new Error(data.error);
              }

              if (data.done) {
                // Message complete
                if (assistantMessage) {
                  saveConversation();
                }
                return;
              }

              if (data.content) {
                hideTypingIndicator();
                assistantMessage += data.content;

                if (!messageElement) {
                  // Create new message element
                  messageElement = createMessageElement('assistant', '');
                  messagesContainer.appendChild(messageElement);
                  conversationHistory.push({ role: 'assistant', content: '' });
                }

                // Update message content
                const contentEl = messageElement.querySelector('.chatbot-message-content') || messageElement;
                contentEl.innerHTML = formatMessage(assistantMessage);
                conversationHistory[conversationHistory.length - 1].content = assistantMessage;
                
                scrollToBottom();
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete JSON
              if (parseError.message !== 'Unexpected end of JSON input') {
                console.error('Parse error:', parseError);
              }
            }
          }
        }
      }
    } catch (streamError) {
      throw streamError;
    }
  }

  // ============================================
  // Message Display
  // ============================================
  function addMessage(role, content) {
    const messageElement = createMessageElement(role, content);
    messagesContainer.appendChild(messageElement);
    
    conversationHistory.push({ role, content });
    
    // Trim history if too long
    if (conversationHistory.length > MAX_HISTORY_LENGTH) {
      conversationHistory = conversationHistory.slice(-MAX_HISTORY_LENGTH);
    }
    
    saveConversation();
    scrollToBottom();
  }

  function createMessageElement(role, content) {
    const div = document.createElement('div');
    div.className = `chatbot-message chatbot-message--${role}`;
    div.innerHTML = `<div class="chatbot-message-content">${formatMessage(content)}</div>`;
    return div;
  }

  function formatMessage(content) {
    if (!content) return '';

    // Escape HTML
    let formatted = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Convert markdown-style formatting
    // Bold
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Italic
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Line breaks
    formatted = formatted.replace(/\n/g, '<br>');

    // Links
    formatted = formatted.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    // Plain URLs
    formatted = formatted.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    return formatted;
  }

  function scrollToBottom() {
    requestAnimationFrame(() => {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    });
  }

  // ============================================
  // UI Helpers
  // ============================================
  function showTypingIndicator() {
    typingIndicator.classList.add('is-visible');
    typingIndicator.setAttribute('aria-hidden', 'false');
    scrollToBottom();
  }

  function hideTypingIndicator() {
    typingIndicator.classList.remove('is-visible');
    typingIndicator.setAttribute('aria-hidden', 'true');
  }

  function showNotification() {
    notification.classList.add('is-visible');
  }

  function hideNotification() {
    notification.classList.remove('is-visible');
  }

  function showError(message) {
    errorText.textContent = message;
    errorContainer.classList.add('is-visible');
    errorContainer.setAttribute('aria-hidden', 'false');
  }

  function hideError() {
    errorContainer.classList.remove('is-visible');
    errorContainer.setAttribute('aria-hidden', 'true');
  }

  // ============================================
  // Event Handlers
  // ============================================
  function handleOutsideClick(e) {
    if (!isOpen) return;
    
    if (!widget.contains(e.target)) {
      closeChat();
    }
  }

  function handleEscapeKey(e) {
    if (e.key === 'Escape' && isOpen) {
      closeChat();
    }
  }

  // ============================================
  // Persistence
  // ============================================
  function saveConversation() {
    try {
      const data = {
        history: conversationHistory.slice(-MAX_HISTORY_LENGTH),
        timestamp: Date.now()
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('Chatbot: Could not save conversation', e);
    }
  }

  function loadConversation() {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        
        // Check if conversation is from this session (within last 2 hours)
        const twoHours = 2 * 60 * 60 * 1000;
        if (Date.now() - data.timestamp < twoHours) {
          conversationHistory = data.history || [];
          hasSeenWelcome = conversationHistory.length > 0;
          
          // Render saved messages
          conversationHistory.forEach(msg => {
            const messageElement = createMessageElement(msg.role, msg.content);
            messagesContainer.appendChild(messageElement);
          });
        } else {
          // Clear old conversation
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (e) {
      console.warn('Chatbot: Could not load conversation', e);
    }
  }

  // ============================================
  // Start
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
